import React, { useState } from "react";
import api from "../../Utils/api";

export default function FileUpload({ title, attachment, onChange, onRemove }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // For image preview locally
  const [attachmentUrl, setAttachmentUrl] = useState(attachment); // URL from server
  const [uploading, setUploading] = useState(false);

  const serverUrl = attachment
    ? process.env.REACT_APP_SERVER_ADDRESS + attachment
    : null;

  // Simulate file upload to server — replace with your actual upload logic
  async function uploadFileToServer(file) {
    try {
      let attachment = new FormData();
      attachment.append("file", file);

      const res = await api.post("/user/employment_attach", attachment, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        // Make sure this is a full URL, or build it if only filename is returned
        const fileUrl = res.data.filename.startsWith("http")
          ? res.data.filename
          : `/uploads/${res.data.filename}`;
        return fileUrl;
      } else {
        throw new Error("Upload failed: server returned failure");
      }
    } catch (error) {
      console.error("Upload error:", error);
      throw error; // rethrow so caller knows upload failed
    }
  }

  async function handleFileChange(e) {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.size > 20 * 1024 * 1024) {
      alert("File size exceeds 20MB limit");
      e.target.value = null;
      return;
    }

    setFile(selectedFile);
    setUploading(true);

    // For images, create local preview URL immediately
    if (selectedFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }

    try {
      // Upload file to server
      const serverUrl = await uploadFileToServer(selectedFile);
      onChange(serverUrl);
      setAttachmentUrl(serverUrl);
    } catch (error) {
      alert("Upload failed, please try again.");
      setFile(null);
      setPreviewUrl(null);
      setAttachmentUrl(null);
      e.target.value = null;
    } finally {
      setUploading(false);
    }
  }

  function handleRemove() {
    setFile(null);
    setPreviewUrl(null);
    setAttachmentUrl(null);
  }

  function openFile() {
    if (!file) return;

    // If uploaded file URL exists, open that
    if (attachmentUrl) {
      window.open(attachmentUrl, "_blank");
    } else if (previewUrl) {
      // fallback: open local preview URL (image)
      window.open(previewUrl, "_blank");
    }
  }

  const renderDocIcon = () => (
    <div
      className="w-20 h-24 flex flex-col items-center justify-center border border-gray-300 rounded-md bg-gray-50 cursor-pointer relative"
      onClick={openFile}
      title={file.name}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8 text-gray-600 mb-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      <span className="text-xs text-gray-700 truncate max-w-full px-1 text-center">
        {file.name}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
        className="absolute top-1 right-1 text-red-600 hover:text-red-800 font-bold"
        aria-label="Remove file"
      >
        ×
      </button>
    </div>
  );

  return (
    <div>
      {!attachmentUrl && (
        <div className="flex items-center gap-3 text-sm">
          <label
            htmlFor={`attachment-${title}`}
            className={`cursor-pointer px-3 py-1.5 rounded-md font-medium ${
              uploading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {uploading ? "Uploading..." : "Add"}
          </label>
          <input
            id={`attachment-${title}`}
            type="file"
            className="hidden"
            accept=".jpeg,.jpg,.png,.csv,.doc,.docx,.xls,.xlsx,.pdf"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <span className="text-xs text-gray-500">
            Accepted: JPEG, PNG, DOC, DOCX, PDF (Max 20MB)
          </span>
        </div>
      )}

      {attachmentUrl && !file && (
        <div className="relative mt-2 flex items-center">
          {/* Show preview if image */}
          {serverUrl.endsWith(".jpg") ||
          serverUrl.endsWith(".jpeg") ||
          serverUrl.endsWith(".png") ? (
            <div
              className="relative cursor-pointer h-24 rounded-md overflow-hidden border border-gray-300"
              title={attachment}
              onClick={() => window.open(serverUrl, "_blank")}
            >
              <img
                src={serverUrl}
                alt="attachment"
                className="object-cover w-full h-full"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentUrl(null);
                }}
                className="absolute top-1 right-1 bg-white rounded-full px-1.5 py-0.5 text-red-600 hover:text-red-800 font-bold shadow"
                aria-label="Remove file"
              >
                ×
              </button>
            </div>
          ) : (
            // Show a generic doc icon for non-images
            <div
              onClick={() => window.open(serverUrl, "_blank")}
              className="w-20 h-24 flex flex-col items-center justify-center border border-gray-300 rounded-md bg-gray-50 cursor-pointer relative"
              title={attachment}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-600 mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span className="text-xs text-gray-700 truncate max-w-full px-1 text-center">
                {attachment.split("/").pop()}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setAttachmentUrl(null);
                }}
                className="absolute top-1 right-1 text-red-600 hover:text-red-800 font-bold"
                aria-label="Remove file"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {file && (
        <div className="relative mt-2 flex items-center">
          {previewUrl ? (
            <div
              onClick={openFile}
              className="relative cursor-pointer h-24 rounded-md overflow-hidden border border-gray-300"
              title={file.name}
            >
              <img
                src={previewUrl}
                alt={file.name}
                className="object-cover w-full h-full"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute top-1 right-1 bg-white rounded-full px-1.5 py-0.5 text-red-600 hover:text-red-800 font-bold shadow"
                aria-label="Remove file"
              >
                ×
              </button>
            </div>
          ) : (
            renderDocIcon()
          )}
        </div>
      )}
    </div>
  );
}
